// Posting service health: the one definition of what "online" means, and the badge
// that says it. Status derives from `serviceLastSeenAt` alone.
//
// Vocabulary is deliberate and app-wide: a posting service is "Online", "Offline"
// (red — same word as the Overview card and the Problems section), or "Never
// connected"; the word "Stale" is reserved for estimate freshness, never for
// the posting service.
//
// The window is exported because the Overview's cards judge the same
// question. Three private copies of it used to exist, and had one drifted
// the locations badge and the Problems card would have disagreed about the same
// location — the sort of contradiction that costs an operations UI its
// credibility.

import { Badge } from "@pej/kore/components";

export const SERVICE_ONLINE_WINDOW_MS = 5 * 60 * 1000;

export type ServiceHealthStatus = "online" | "offline" | "never";

/** Pure so the three states are trivially testable without faking timers. */
export function serviceHealthStatus(serviceLastSeenAt: string | null, now: Date = new Date()): ServiceHealthStatus {
    if (!serviceLastSeenAt) return "never";
    const elapsed = now.getTime() - new Date(serviceLastSeenAt).getTime();
    return elapsed <= SERVICE_ONLINE_WINDOW_MS ? "online" : "offline";
}

const STATUS_TONE = {
    online: "success",
    offline: "danger",
    never: "neutral",
} as const;

const STATUS_LABEL: Record<ServiceHealthStatus, string> = {
    online: "Online",
    offline: "Offline",
    never: "Never connected",
};

export interface ServiceStatusBadgeProps {
    status: ServiceHealthStatus;
}

export function ServiceStatusBadge({ status }: ServiceStatusBadgeProps) {
    return (
        <Badge tone={STATUS_TONE[status]} dot>
            {STATUS_LABEL[status]}
        </Badge>
    );
}
